import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Activity, AlertTriangle, ArrowDownRight, ArrowUpRight, Banknote, BookOpen,
  CalendarCheck, Download, FileSpreadsheet, FileText, GraduationCap, Heart,
  Loader2, Megaphone, Sparkles, TrendingUp, Users, Wallet,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend, RadialBarChart, RadialBar,
} from "recharts";
import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { AICallout } from "@/components/makai/primitives";
import { requireRole } from "@/lib/session";
import { useSchoolContext } from "@/lib/school-context";
import { executiveDashboard } from "@/lib/executive-dashboard.functions";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/executive")({
  ssr: false,
  beforeLoad: () => requireRole("admin"),
  head: () => ({ meta: [{ title: "Executive Dashboard · MAKAI" }] }),
  component: Page,
});

const UGX = (n: number) => "UGX " + new Intl.NumberFormat("en-UG").format(Math.round(n || 0));
const NUM = (n: number) => new Intl.NumberFormat("en-UG").format(Math.round(n || 0));
const COLORS = ["#C8A03C", "#0B1736", "#1976D2", "#2E7D32", "#D32F2F", "#F9A825", "#7C3AED", "#0F766E"];

function Page() {
  const { active, loading } = useSchoolContext();
  return (
    <AdminShell
      title="Executive Dashboard"
      subtitle="Owner-level KPIs, forecasts and operational health across your school."
    >
      {loading ? <Loading /> : !active?.school_id ? <NoSchool /> : (
        <Dashboard key={active.school_id} schoolId={active.school_id} schoolName={active.school?.name ?? "School"} />
      )}
    </AdminShell>
  );
}
function Loading() { return <div className="rounded-2xl border bg-card p-10 text-center text-sm text-muted-foreground">Loading…</div>; }
function NoSchool() { return <div className="rounded-2xl border bg-card p-10 text-center text-sm text-muted-foreground">Select a school to view the executive dashboard.</div>; }

function Dashboard({ schoolId, schoolName }: { schoolId: string; schoolName: string }) {
  const fetchFn = useServerFn(executiveDashboard);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["executive-dashboard", schoolId],
    queryFn: () => fetchFn({ data: { schoolId } }),
  });
  const printRef = useRef<HTMLDivElement>(null);

  if (isLoading) return <Loading />;
  if (error) return <div className="rounded-2xl border bg-card p-8 text-sm text-destructive">Failed to load: {(error as Error).message}</div>;
  if (!data) return null;

  const { kpis, fees, attendance, subjects, atRisk, revenue, admissions, teachers, growth, csat, leads, forecast } = data;

  const enrollmentChart = useMemo(() => {
    const hist = admissions.series.map((r: any) => ({ month: r.month, actual: r.enrolled, forecast: null as number | null }));
    const fut = forecast.map((r: any) => ({ month: r.month, actual: null as number | null, forecast: r.forecast }));
    return [...hist, ...fut];
  }, [admissions, forecast]);

  const exportExcel = async () => {
    const XLSX = await import("xlsx");
    const wb = XLSX.utils.book_new();
    const add = (name: string, rows: any[]) => XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), name.slice(0, 31));
    add("KPIs", [{
      "Active learners": kpis.activeLearners, "Active staff": kpis.activeStaff,
      "Fees billed (UGX)": kpis.feesBilledUgx, "Fees collected (UGX)": kpis.feesCollectedUgx,
      "Outstanding (UGX)": kpis.feesOutstandingUgx, "Overdue invoices": kpis.overdueInvoices,
      "Avg assessment %": kpis.avgAssessmentScorePct ?? 0, "Attendance % (30d)": kpis.attendancePct30d ?? 0,
      "Generated": data.generatedAt,
    }]);
    add("Revenue", revenue.monthly);
    add("Cash flow", revenue.cashFlow);
    add("Revenue by method", revenue.revenueByMethod);
    add("Admissions", admissions.series);
    add("Enrollment forecast", forecast);
    add("Attendance trend", attendance.trend);
    add("Subjects", subjects);
    add("Teachers (top)", teachers);
    add("Student growth", growth);
    add("Parent satisfaction", csat.distribution.map((d: any) => ({ stars: d.star, count: d.count })));
    add("Marketing leads", leads.series);
    add("At-risk learners", atRisk);
    XLSX.writeFile(wb, `executive-dashboard-${schoolName.replace(/\s+/g, "_")}-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportPdf = async () => {
    const jsPDF = (await import("jspdf")).default;
    const autoTable = (await import("jspdf-autotable")).default;
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const w = doc.internal.pageSize.getWidth();
    doc.setFillColor(11, 23, 54); doc.rect(0, 0, w, 70, "F");
    doc.setTextColor(200, 160, 60); doc.setFontSize(20);
    doc.text("MAKAI · Executive Dashboard", 40, 35);
    doc.setTextColor(247, 246, 240); doc.setFontSize(11);
    doc.text(`${schoolName} — ${new Date(data.generatedAt).toLocaleString()}`, 40, 55);
    doc.setTextColor(20, 20, 20);

    autoTable(doc, {
      startY: 90, theme: "grid",
      head: [["KPI", "Value"]],
      body: [
        ["Active learners", NUM(kpis.activeLearners)],
        ["Active staff", NUM(kpis.activeStaff)],
        ["Revenue (12m)", UGX(revenue.revenue12m)],
        ["Fees billed", UGX(kpis.feesBilledUgx)],
        ["Fees collected", UGX(kpis.feesCollectedUgx)],
        ["Outstanding fees", UGX(kpis.feesOutstandingUgx)],
        ["Overdue invoices", NUM(kpis.overdueInvoices)],
        ["Attendance (30d)", `${kpis.attendancePct30d ?? 0}%`],
        ["Avg assessment", `${kpis.avgAssessmentScorePct ?? 0}%`],
        ["Admissions (12m)", `${NUM(admissions.totalApps12m)} apps · ${NUM(admissions.totalEnrolled12m)} enrolled`],
        ["Marketing leads (6m)", `${NUM(leads.totalLeads)} · ${leads.conversionPct}% conv.`],
        ["Parent CSAT", `${csat.csatPct}% (NPS ${csat.nps})`],
        ["AI risk alerts", `${atRisk.length} learners flagged`],
      ],
      headStyles: { fillColor: [11, 23, 54], textColor: [200, 160, 60] },
      styles: { fontSize: 10 },
    });

    autoTable(doc, {
      head: [["Month", "Revenue (UGX)", "Billed (UGX)"]],
      body: revenue.monthly.map((m: any) => [m.month, UGX(m.revenue), UGX(m.billed)]),
      headStyles: { fillColor: [200, 160, 60], textColor: [11, 23, 54] }, styles: { fontSize: 9 },
    });

    autoTable(doc, {
      head: [["Teacher", "Avg class %", "Samples"]],
      body: teachers.map((t: any) => [t.name, `${t.avgPct}%`, NUM(t.samples)]),
      headStyles: { fillColor: [200, 160, 60], textColor: [11, 23, 54] }, styles: { fontSize: 9 },
    });

    autoTable(doc, {
      head: [["At-risk learner", "Avg %", "Attendance %", "Reasons"]],
      body: atRisk.map((r: any) => [r.name, r.avgScorePct ?? "—", r.attendancePct ?? "—", r.reasons.join("; ")]),
      headStyles: { fillColor: [211, 47, 47], textColor: [255, 255, 255] }, styles: { fontSize: 9 },
    });

    doc.save(`executive-dashboard-${schoolName.replace(/\s+/g, "_")}-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const collectionRate = fees.collectionRatePct;
  const revGrowth = revenueGrowthPct(revenue.monthly);
  const enrolGrowth = pct(growth[growth.length - 1]?.learners, growth[0]?.learners);

  return (
    <div ref={printRef} className="space-y-6">
      {/* Header bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-gradient-to-r from-[#0B1736] to-[#1a2b5c] p-5 text-[#F7F6F0] shadow-soft">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#C8A03C]">Owner view</div>
          <div className="font-display text-xl font-bold">{schoolName}</div>
          <div className="text-xs opacity-80">Updated {new Date(data.generatedAt).toLocaleString()}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => refetch()} variant="secondary" className="bg-white/10 text-white hover:bg-white/20">
            <Activity className="mr-2 h-4 w-4" /> Refresh
          </Button>
          <Button onClick={exportExcel} className="bg-[#2E7D32] text-white hover:bg-[#256829]">
            <FileSpreadsheet className="mr-2 h-4 w-4" /> Export Excel
          </Button>
          <Button onClick={exportPdf} className="bg-[#C8A03C] text-[#0B1736] hover:bg-[#b68f30]">
            <FileText className="mr-2 h-4 w-4" /> Export PDF
          </Button>
        </div>
      </div>

      {/* KPI grid (Power BI style) */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
        <Kpi label="Revenue (12m)" value={UGX(revenue.revenue12m)} delta={revGrowth} icon={Banknote} tint="emerald" />
        <Kpi label="Outstanding Fees" value={UGX(kpis.feesOutstandingUgx)} sub={`${kpis.overdueInvoices} overdue invoices`} icon={Wallet} tint="amber" inverse />
        <Kpi label="Collection Rate" value={`${collectionRate}%`} sub={`${UGX(kpis.feesCollectedUgx)} collected`} icon={TrendingUp} tint="emerald" />
        <Kpi label="Admissions (12m)" value={NUM(admissions.totalApps12m)} sub={`${NUM(admissions.totalEnrolled12m)} enrolled`} icon={GraduationCap} tint="indigo" />
        <Kpi label="Attendance (30d)" value={`${kpis.attendancePct30d ?? 0}%`} icon={CalendarCheck} tint="emerald" />
        <Kpi label="Exam Performance" value={`${kpis.avgAssessmentScorePct ?? 0}%`} sub="Avg assessment" icon={BookOpen} tint="indigo" />
        <Kpi label="Student Growth" value={NUM(growth[growth.length - 1]?.learners ?? 0)} delta={enrolGrowth} icon={Users} tint="emerald" />
        <Kpi label="Active Staff" value={NUM(kpis.activeStaff)} icon={Sparkles} tint="indigo" />
        <Kpi label="Marketing Leads (6m)" value={NUM(leads.totalLeads)} sub={`${leads.conversionPct}% conversion`} icon={Megaphone} tint="indigo" />
        <Kpi label="Parent CSAT" value={`${csat.csatPct}%`} sub={`NPS ${csat.nps}`} icon={Heart} tint="emerald" />
        <Kpi label="AI Risk Alerts" value={NUM(atRisk.length)} sub="learners flagged" icon={AlertTriangle} tint="rose" inverse />
        <Kpi label="Enrollment Forecast (6m)" value={NUM(forecast.reduce((s: number, r: any) => s + r.forecast, 0))} icon={TrendingUp} tint="emerald" />
      </div>

      {/* Revenue & cash flow */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Revenue trend" subtitle="Monthly inflow (UGX)" span={2}>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={revenue.monthly}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C8A03C" stopOpacity={0.85} />
                  <stop offset="100%" stopColor="#C8A03C" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => compact(Number(v))} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => UGX(v)} />
              <Area type="monotone" dataKey="revenue" stroke="#C8A03C" strokeWidth={2} fill="url(#rev)" />
              <Line type="monotone" dataKey="billed" stroke="#0B1736" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Revenue by method">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={revenue.revenueByMethod} dataKey="amount" nameKey="method" innerRadius={55} outerRadius={95} paddingAngle={2}>
                {revenue.revenueByMethod.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v: number) => UGX(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card title="Cash flow" subtitle="Inflow vs outflow & cumulative position">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={revenue.cashFlow}>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={(v) => compact(Number(v))} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v: number) => UGX(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="inflow" fill="#2E7D32" radius={[4, 4, 0, 0]} />
            <Bar dataKey="outflow" fill="#D32F2F" radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="cumulative" stroke="#C8A03C" strokeWidth={3} dot={{ r: 3 }} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Admissions + forecast */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Admissions" subtitle="Applications vs enrollments (12 months)">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={admissions.series}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="applications" fill="#1976D2" radius={[4, 4, 0, 0]} />
              <Bar dataKey="enrolled" fill="#C8A03C" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Enrollment forecast" subtitle="Next 6 months · linear projection">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={enrollmentChart}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="actual" stroke="#0B1736" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="forecast" stroke="#C8A03C" strokeWidth={2.5} strokeDasharray="6 4" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Attendance + exam perf */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Attendance trend" subtitle="Weekly % (last 12 weeks)">
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={attendance.trend}>
              <defs>
                <linearGradient id="att" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2E7D32" stopOpacity={0.7} />
                  <stop offset="100%" stopColor="#2E7D32" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="week" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => `${v}%`} />
              <Area type="monotone" dataKey="attendancePct" stroke="#2E7D32" fill="url(#att)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Exam performance by subject" subtitle="Average score %">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={subjects.slice(0, 10)} layout="vertical" margin={{ left: 60 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="subject" tick={{ fontSize: 11 }} width={110} />
              <Tooltip formatter={(v: number) => `${v}%`} />
              <Bar dataKey="avgPct" fill="#1976D2" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Teacher perf + student growth */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Top teacher performance" subtitle="Avg class score % (120d)">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={teachers} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
              <Tooltip formatter={(v: number) => `${v}%`} />
              <Bar dataKey="avgPct" fill="#C8A03C" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Student growth" subtitle="Active learners trend">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={growth}>
              <defs>
                <linearGradient id="grow" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1976D2" stopOpacity={0.7} />
                  <stop offset="100%" stopColor="#1976D2" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Area type="monotone" dataKey="learners" stroke="#1976D2" fill="url(#grow)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Marketing leads + Parent CSAT */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Marketing funnel" subtitle="Leads vs converted (6m)" span={2}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={leads.series}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="leads" fill="#7C3AED" radius={[4, 4, 0, 0]} />
              <Bar dataKey="converted" fill="#2E7D32" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Parent satisfaction" subtitle={`CSAT ${csat.csatPct}% · NPS ${csat.nps}`}>
          <ResponsiveContainer width="100%" height={240}>
            <RadialBarChart innerRadius="40%" outerRadius="100%" data={[{ name: "csat", value: csat.csatPct, fill: "#C8A03C" }]} startAngle={90} endAngle={-270}>
              <RadialBar dataKey="value" cornerRadius={12} background={{ fill: "hsl(var(--muted))" }} />
              <Tooltip />
            </RadialBarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* AI Risk Alerts */}
      <Card title="AI Risk Alerts" subtitle="Learners flagged by performance + attendance signals">
        {atRisk.length === 0 ? (
          <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">No learners currently flagged. 🎉</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr><th className="py-2">Learner</th><th>Adm #</th><th>Avg %</th><th>Attendance</th><th>Reasons</th></tr>
              </thead>
              <tbody>
                {atRisk.map((r: any, i: number) => (
                  <tr key={i} className="border-t">
                    <td className="py-2 font-medium">{r.name}</td>
                    <td className="text-muted-foreground">{r.admissionNo ?? "—"}</td>
                    <td><Pill value={r.avgScorePct} threshold={50} suffix="%" /></td>
                    <td><Pill value={r.attendancePct} threshold={75} suffix="%" /></td>
                    <td className="text-xs text-muted-foreground">{r.reasons.join("; ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AICallout
        description="Ask anything about the school's performance, finances or risks."
        suggestions={[
          "Explain this dashboard",
          "Generate an executive summary",
          "Which trend should I act on first?",
          "Predict next month's revenue",
        ]}
      />
    </div>
  );
}

function compact(n: number) {
  if (Math.abs(n) >= 1e9) return (n / 1e9).toFixed(1) + "B";
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(n);
}
function pct(curr?: number, prev?: number): number | null {
  if (!curr || !prev) return null;
  return Math.round(((curr - prev) / prev) * 1000) / 10;
}
function revenueGrowthPct(monthly: { revenue: number }[]): number | null {
  if (monthly.length < 2) return null;
  const last = monthly[monthly.length - 1].revenue;
  const prev = monthly[monthly.length - 2].revenue;
  return pct(last, prev);
}

function Kpi({ label, value, sub, delta, icon: Icon, tint, inverse }: {
  label: string; value: string; sub?: string; delta?: number | null;
  icon: typeof Activity; tint: "emerald" | "amber" | "rose" | "indigo"; inverse?: boolean;
}) {
  const tints = {
    emerald: "from-emerald-500/15 to-emerald-500/0 text-emerald-700 dark:text-emerald-300",
    amber: "from-amber-500/15 to-amber-500/0 text-amber-700 dark:text-amber-300",
    rose: "from-rose-500/15 to-rose-500/0 text-rose-700 dark:text-rose-300",
    indigo: "from-indigo-500/15 to-indigo-500/0 text-indigo-700 dark:text-indigo-300",
  }[tint];
  const up = (delta ?? 0) >= 0;
  const good = inverse ? !up : up;
  return (
    <div className="group relative overflow-hidden rounded-2xl border bg-card p-4 shadow-soft transition hover:-translate-y-0.5 hover:shadow-md">
      <div className={cn("absolute inset-x-0 top-0 h-1", good ? "bg-[#2E7D32]" : "bg-[#D32F2F]")} />
      <div className={cn("absolute inset-0 -z-0 bg-gradient-to-br opacity-60", tints)} />
      <div className="relative flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="mt-1 font-display text-2xl font-bold">{value}</div>
          {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
        </div>
        <div className={cn("grid h-9 w-9 place-items-center rounded-xl", tints)}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      {delta != null && (
        <div className={cn("relative mt-3 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold",
          good ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
               : "bg-rose-500/15 text-rose-700 dark:text-rose-300")}>
          {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />} {Math.abs(delta)}% MoM
        </div>
      )}
    </div>
  );
}

function Card({ title, subtitle, children, span }: { title: string; subtitle?: string; children: React.ReactNode; span?: number }) {
  return (
    <div className={cn("rounded-2xl border bg-card p-5 shadow-soft", span === 2 && "lg:col-span-2")}>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <div>
          <div className="font-display text-base font-bold">{title}</div>
          {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Pill({ value, threshold, suffix }: { value: number | null; threshold: number; suffix: string }) {
  if (value == null) return <span className="text-muted-foreground">—</span>;
  const ok = value >= threshold;
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-xs font-semibold",
      ok ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "bg-rose-500/15 text-rose-700 dark:text-rose-300")}>
      {value}{suffix}
    </span>
  );
}
